// AstroApiService.js — real D1 (Rasi) chart calculation.
//
// Two different providers, split by what each is actually good for:
//   - Geo Location (place name → lat/long): OpenStreetMap's free Nominatim
//     service, called directly from the browser. navamsha.in has no
//     place-name lookup endpoint at all (checked its full Postman
//     collection — only takes raw lat/long/timezone as input), and
//     freeastrologyapi.com is no longer used anywhere in this file per the
//     client's explicit request. Nominatim needs no API key and allows
//     direct browser calls (Access-Control-Allow-Origin: *, confirmed
//     live). Timezone still isn't something it returns, so this keeps the
//     same IST (+5.5) fallback the old freeastrologyapi.com path used —
//     every existing client project in this app is India-based, so that's
//     a safe default either way.
//   - Planets/Extended (the actual D1 chart — Ascendant + 9 planets' houses):
//     navamsha.in, but called through this app's own backend
//     (POST /api/astro/d1-chart) instead of directly from the browser —
//     navamsha.in's response doesn't include an Access-Control-Allow-Origin
//     header, so a direct browser call is blocked by CORS (confirmed live,
//     reported to navamsha.in). The backend does the actual navamsha.in
//     call server-side, where CORS doesn't apply, and returns the same
//     response shape straight through — see AstroProxyController on the
//     backend. Bonus: the navamsha.in API key now only lives in the
//     backend's .env, not bundled into this frontend's public JS build.
//
// Docs used:
//   Geo Location   — https://nominatim.org/release-docs/latest/api/Search/
//   Planets (D1)   — https://www.navamsha.in/apis → Kundali & Planets API →
//                     POST /api/v1/planets/extended (Postman collection at
//                     https://www.navamsha.in/postman/vedic-astro-api.postman_collection.json)
//                     — proxied via this app's backend, see above.

import { apiCall } from '../../../services/api';

const GEO_API_BASE = 'https://nominatim.openstreetmap.org';

// The classical 9 grahas this app's D1 chart tracks (see GRAHAS in
// VastuPersonsGateway.jsx). The API also returns Uranus/Neptune/Pluto —
// intentionally ignored, Vedic D1 charts here don't place those.
const GRAHA_KEYS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

export class AstroApiError extends Error {}

/**
 * Free-text place name → { latitude, longitude, timezone }.
 * Nominatim's plain `/search` (GET, no key, CORS-open) — ranks results by
 * relevance but occasionally puts a same-named landmark (e.g. a railway
 * station) ahead of the actual city; `accept-language=en` fixes that in
 * practice (verified live) by making it prefer the named settlement itself.
 */
export const resolveBirthPlace = async (placeName) => {
  if (!placeName || !placeName.trim()) {
    throw new AstroApiError('Birth place is required to calculate the D1 chart.');
  }
  const params = new URLSearchParams({
    q: placeName.trim(),
    format: 'json',
    limit: '1',
    'accept-language': 'en',
  });

  let res;
  try {
    res = await fetch(`${GEO_API_BASE}/search?${params.toString()}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
  } catch (e) {
    throw new AstroApiError('Could not reach the location lookup service — check your internet connection.');
  }
  if (!res.ok) {
    throw new AstroApiError(`Location lookup failed (${res.status}). Please try again.`);
  }
  const json = await res.json().catch(() => null);
  const row = Array.isArray(json) ? json[0] : null;
  if (!row || row.lat == null || row.lon == null) {
    throw new AstroApiError(`Could not find "${placeName}" — try a more specific place (e.g. add city/state/country).`);
  }
  return {
    latitude: parseFloat(row.lat),
    longitude: parseFloat(row.lon),
    // Nominatim doesn't return a timezone — falls back to IST, same as
    // before; every existing client project in this app is India-based.
    timezone: 5.5,
    resolvedName: row.display_name || placeName,
  };
};

/**
 * Shared birth-input resolution: validates dob/tob/place, geocodes the
 * place via Nominatim, and returns the flat shape every navamsha.in-backed
 * endpoint in this file needs. Each of the functions below calls this
 * separately (a small repeat geocode lookup) rather than threading resolved
 * coordinates through the UI layer — keeps every call here self-contained
 * with the same simple `{ dob, tob, place }` input the existing D1 chart UI
 * already collects per person.
 */
const resolveBirthInput = async ({ dob, tob, place, placeGeo }) => {
  if (!dob) throw new AstroApiError('Date of birth is required.');
  if (!tob) throw new AstroApiError('Time of birth is required for an accurate calculation.');
  if (!place) throw new AstroApiError('Place of birth is required.');

  const [year, month, date] = dob.split('-').map(Number);
  const [hours, minutes] = tob.split(':').map(Number);
  // A place picked from the autocomplete dropdown already carries its own
  // coordinates + timezone — use them as-is instead of re-guessing the free
  // text through Nominatim (whose top hit could be a different same-named
  // place). Free-typed places (no pick) still fall back to the old lookup.
  const hasPicked = placeGeo && Number.isFinite(Number(placeGeo.latitude)) && Number.isFinite(Number(placeGeo.longitude));
  const { latitude, longitude, timezone, resolvedName } = hasPicked
    ? {
        latitude: Number(placeGeo.latitude),
        longitude: Number(placeGeo.longitude),
        timezone: placeGeo.timezone != null ? Number(placeGeo.timezone) : 5.5,
        resolvedName: placeGeo.label || place,
      }
    : await resolveBirthPlace(place);

  return {
    year, month, date, hours, minutes, seconds: 0,
    latitude, longitude, timezone, resolvedPlace: resolvedName,
  };
};

/** Posts to one of this backend's /astro/* proxy routes (see file header for why it's a proxy, not a direct navamsha.in call). */
const callAstroProxy = async (endpoint, birth) => {
  try {
    return await apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify({
        year: birth.year, month: birth.month, date: birth.date,
        hours: birth.hours, minutes: birth.minutes, seconds: birth.seconds ?? 0,
        latitude: birth.latitude, longitude: birth.longitude, timezone: birth.timezone,
      }),
    });
  } catch (e) {
    throw new AstroApiError(e.message || 'Astro API request failed.');
  }
};

/**
 * Computes a real D1 (Rasi/Lagna) chart and reshapes the API response into
 * this app's existing astroData shape:
 *   { lagnaRashi: 1-12, houses: { [houseNum]: ['Sun','Moon',...] } }
 * — exactly what the manual Lagna-picker + Graha-checkbox UI already
 * produces (see VastuPersonsGateway.jsx), so the D1Chart component and PDF
 * export need no changes at all — only the data source changes.
 */
export const computeD1Chart = async ({ dob, tob, place, placeGeo }) => {
  const birth = await resolveBirthInput({ dob, tob, place, placeGeo });
  const json = await callAstroProxy('/astro/d1-chart', birth);

  const output = json && (json.output || json);
  if (!output || !output.Ascendant) {
    throw new AstroApiError('Astro API did not return chart data — double-check the API key and try again.');
  }

  const lagnaRashi = output.Ascendant.current_sign;
  // Degree the Ascendant sits at *within* its own sign (0-30°) — the API
  // returns this directly as normDegree. Needed for the AstroVastu Chart,
  // whose per-house compass windows are offset by exactly this amount
  // instead of assuming the Ascendant sits at a clean 0° (see
  // VastuChartVariants.js).
  const ascendantNormDegree = output.Ascendant.normDegree ?? 0;
  const houses = {};
  GRAHA_KEYS.forEach((key) => {
    const p = output[key];
    if (!p || !p.house_number) return;
    if (!houses[p.house_number]) houses[p.house_number] = [];
    houses[p.house_number].push(key);
  });

  // Janma (birth) Nakshatra = Moon's Nakshatra at birth — feeds the Navatara
  // Chakra calculation (KundaliReportsData.js#computeNavataraChakra).
  const moonNakshatraNumber = output.Moon?.nakshatra_number ?? null;

  return {
    lagnaRashi, houses, ascendantNormDegree, moonNakshatraNumber,
    resolvedPlace: birth.resolvedPlace, latitude: birth.latitude,
    longitude: birth.longitude, timezone: birth.timezone,
  };
};

/**
 * Full Vimshottari Mahadasha timeline for the Kundali report's Vimsottari
 * Table. Returns navamsha.in's response straight through — see
 * AstroProxyController#vimshottariDasha and the Postman collection for the
 * exact shape (`output.mahadashas[]`, each `{ lord, start, end, duration_days }`).
 */
export const getVimshottariDasha = async ({ dob, tob, place, placeGeo }) => {
  const birth = await resolveBirthInput({ dob, tob, place, placeGeo });
  const json = await callAstroProxy('/astro/vimshottari-dasha', birth);
  const output = json && (json.output || json);
  if (!output || !Array.isArray(output.mahadashas)) {
    throw new AstroApiError('Astro API did not return a Dasha timeline — please try again.');
  }
  return output;
};

/**
 * Bhinnashtakavarga point totals (per planet, per sign) plus the combined
 * Sarvashtakavarga — the raw numbers behind the Kundali report's
 * General House / Office Activity Mapping tables. Feed the result straight
 * into KundaliReportsData.js#buildActivityMapping.
 */
export const getAshtakavarga = async ({ dob, tob, place, placeGeo }) => {
  const birth = await resolveBirthInput({ dob, tob, place, placeGeo });
  const json = await callAstroProxy('/astro/ashtakavarga', birth);
  const output = json && (json.output || json);
  if (!output || !output.bav) {
    throw new AstroApiError('Astro API did not return Ashtakavarga data — please try again.');
  }
  return output;
};

/**
 * KP (Krishnamurti Paddhati) chart — all 9 planet longitudes + all 12 house
 * cusp longitudes in one call. This is the raw geometry the "Hit Theory"
 * report's two grids (Planet↔Planet, Planet↔House) are built from — see
 * KundaliReportsData.js#HIT_MASTER/calculateHit and
 * VastuPersonsGateway.jsx's HitTheorySection. The actual HIT angle
 * classification happens client-side against those raw longitudes; this
 * function only fetches them.
 */
export const getKpChart = async ({ dob, tob, place, placeGeo }) => {
  const birth = await resolveBirthInput({ dob, tob, place, placeGeo });
  const json = await callAstroProxy('/astro/kp-chart', birth);
  const output = json && (json.output || json);
  if (!output || !output.positions || !output.cusps) {
    throw new AstroApiError('Astro API did not return KP chart data — please try again.');
  }

  const planets = {};
  GRAHA_KEYS.forEach((key) => {
    const p = output.positions[key];
    if (p && p.longitude != null) planets[key] = Number(p.longitude);
  });

  const cusps = {};
  for (let house = 1; house <= 12; house++) {
    const c = output.cusps[String(house)];
    if (c && c.longitude != null) cusps[house] = Number(c.longitude);
  }

  return { planets, cusps };
};

export default { resolveBirthPlace, computeD1Chart, getVimshottariDasha, getAshtakavarga, getKpChart, AstroApiError };
